import axios from "axios";
import { useQuery, useMutation } from "@tanstack/react-query";

export interface ProfileType {
  value: string;
  label: string;
  count: number;
}

export interface Profile {
  id: number;
  name: string;
  user_id: number | null;
  user_name: string | null;
  user_email: string | null;
}

export interface AuthStatus {
  admin: boolean;
  user: boolean;
  impersonated: boolean;
  impersonated_user_id: number | null;
  impersonated_user_name: string | null;
  impersonated_profile_name: string | null;
}

export interface ImpersonateResult {
  conversation_id: number;
  redirect_url: string;
}

const BASE_URL = "/system/agentic/impersonate";

export const impersonateApi = {
  getProfileTypes: async (): Promise<ProfileType[]> => {
    const { data } = await axios.get(`${BASE_URL}/profile_types.json`);
    return data;
  },

  getProfiles: async (type: string, q?: string): Promise<Profile[]> => {
    const params = new URLSearchParams({ type });
    if (q) params.append("q", q);
    const { data } = await axios.get(`${BASE_URL}/profiles.json?${params}`);
    return data;
  },

  getStatus: async (): Promise<AuthStatus> => {
    const { data } = await axios.get(`${BASE_URL}/status.json`);
    return data;
  },

  start: async (type: string, profileId: number): Promise<ImpersonateResult> => {
    const { data } = await axios.post(`${BASE_URL}/start.json`, {
      type,
      profile_id: profileId,
    });
    return data;
  },

  stop: async (): Promise<{ success: boolean }> => {
    const { data } = await axios.post(`${BASE_URL}/stop.json`);
    return data;
  },
};

export const impersonateKeys = {
  profileTypes: ["impersonate", "profileTypes"] as const,
  profiles: (type: string, q?: string) => ["impersonate", "profiles", type, q] as const,
  status: ["impersonate", "status"] as const,
};

export function useProfileTypes() {
  return useQuery({
    queryKey: impersonateKeys.profileTypes,
    queryFn: impersonateApi.getProfileTypes,
  });
}

export function useProfiles(type: string | null, q?: string) {
  return useQuery({
    queryKey: impersonateKeys.profiles(type || "", q),
    queryFn: () => impersonateApi.getProfiles(type!, q),
    enabled: !!type,
  });
}

export function useAuthStatus() {
  return useQuery({
    queryKey: impersonateKeys.status,
    queryFn: impersonateApi.getStatus,
  });
}

export function useStartImpersonation() {
  return useMutation({
    mutationFn: ({ type, profileId }: { type: string; profileId: number }) =>
      impersonateApi.start(type, profileId),
  });
}

export function useStopImpersonation() {
  return useMutation({
    mutationFn: impersonateApi.stop,
  });
}
